import type {
  AccountingSettings,
  PostingCatalog as ServerPostingCatalog,
  PostingCoverage,
  Label,
} from '@polatkan/api/services';

// Shared shapes come straight from the API package (type-only), so the page
// and the server can never drift.
export type { EventCoverage, LegCoverage, CoverageRow, RoleCoverage, Label } from '@polatkan/api/services';
import type { EventCoverage, CoverageRow } from '@polatkan/api/services';

export type PostingCatalog = Pick<ServerPostingCatalog, 'areas' | 'dimensions' | 'roles' | 'events'>;
export type PostingSettings = AccountingSettings;

export interface RuleRow {
  id: string;
  app: PostingApp;
  event: string;
  role: string;
  dimension: string;
  isActive: boolean;
  notes: string | null;
  updatedAt: Date;
  account: { id: string; code: string; name: string; isActive: boolean } | null;
}

/** Server payload of postingRules.overview. */
export interface PostingOverview {
  app: PostingApp;
  catalog: PostingCatalog;
  rules: RuleRow[];
  settings: PostingSettings;
  coverage: PostingCoverage;
  unresolvedFailures: number;
}

export type PostingApp = 'TRAVEL' | 'TRADING';
export type Lang = 'tr' | 'en';

export const GLOBAL_EVENT = '*';
export const ANY_DIMENSION = '*';

export function langOf(language: string | undefined): Lang {
  return language?.toLowerCase().startsWith('tr') ? 'tr' : 'en';
}

export function pick(label: Label | undefined, lang: Lang): string {
  if (!label) return '';
  return label[lang] || label.en || label.tr;
}

/** "method:CASH|currency:EUR" → "Nakit · EUR" using the catalog's value labels. */
export function dimensionLabel(dimension: string, catalog: PostingCatalog, lang: Lang, anyLabel: string): string {
  if (dimension === ANY_DIMENSION) return anyLabel;
  return dimension
    .split('|')
    .map((segment) => {
      const idx = segment.indexOf(':');
      const key = segment.slice(0, idx);
      const value = segment.slice(idx + 1);
      const def = catalog.dimensions.find((d) => d.key === key);
      const v = def?.values.find((x) => x.value === value);
      return v ? pick(v.label, lang) : `${key}=${value}`;
    })
    .join(' · ');
}

export function parseDimension(dimension: string): Record<string, string> {
  if (dimension === ANY_DIMENSION || !dimension) return {};
  const parts: Record<string, string> = {};
  for (const segment of dimension.split('|')) {
    const idx = segment.indexOf(':');
    if (idx > 0) parts[segment.slice(0, idx)] = segment.slice(idx + 1);
  }
  return parts;
}

/** Canonical dimension string from parts, keys in the role's declared order. */
export function formatDimension(parts: Record<string, string | undefined>, order: readonly string[]): string {
  const singles = order.filter((k) => parts[k]).map((k) => `${k}:${parts[k]}`);
  return singles.length === 0 ? ANY_DIMENSION : singles.join('|');
}

export type SlotStatus = 'rule' | 'default' | 'missing';

export function rowStatus(row: CoverageRow): SlotStatus {
  if (!row.resolved) return 'missing';
  return row.resolved.source === 'rule' ? 'rule' : 'default';
}

/** Does the chosen account look like it belongs to this role (soft TDHP prefix hint)? */
export function matchesExpectedPrefix(code: string | undefined, expectedPrefixes: string[]): boolean {
  if (!code || expectedPrefixes.length === 0) return true;
  return expectedPrefixes.some((p) => code.startsWith(p));
}

export interface AccountOption {
  value: string;
  label: string;
  keywords: string[];
  code: string;
}

export function toAccountOptions(accounts: Array<{ id: string; code: string; name: string }>): AccountOption[] {
  return accounts.map((a) => ({ value: a.id, label: `${a.code} · ${a.name}`, keywords: [a.code, a.name], code: a.code }));
}

export function groupByArea(events: EventCoverage[], catalog: PostingCatalog): Array<{ area: PostingCatalog['areas'][number]; events: EventCoverage[] }> {
  return catalog.areas
    .map((area) => ({ area, events: events.filter((e) => e.area === area.key) }))
    .filter((g) => g.events.length > 0);
}
